import { CrmFieldService, StandardIds, SystemFieldIds } from '@galaxy/crm/services/field-service';
import { DestroyRef, Inject, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import {
  ActivityInterface,
  DateDefault,
  FilterGroupInterface,
  FilterGroupOperator,
  FilterInterface,
  FilterOperator,
  ListActivitiesRequestInterface,
  ListActivitiesResponseInterface,
  PagedResponseMetadataInterface,
} from '@vendasta/crm';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { IAMService, User } from '@vendasta/iamv2';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  scan,
  skipWhile,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ACTIVITIES_TIMELINE_PAGE_SIZE } from '../../../constants';
import {
  ActivityTaskStatus,
  CrmDependencies,
  CrmInjectionToken,
  ObjectAssociationType,
  ObjectType,
  SimplifiedUser,
} from '../../../tokens-and-interfaces';
import { ActivityService, CrmObjectService } from '../../../shared-services';
import { getMultiUsers } from '../../../utils';
import { CacheService } from '@galaxy/crm/components/cache';
import { ContactService } from '../../../shared-services/crm-services/crm-object-service/_contact.service';
import { CompanyService } from '../../../shared-services/crm-services/crm-object-service/_company.service';

export interface ActivityExtended extends ActivityInterface {
  ownerUser?: User;
  ownerUserLoading?: boolean;
  isLoggedActivity?: boolean;
}

interface ActivityExtendedWithEffectiveDate extends ActivityExtended {
  effectiveDate: Date;
}

interface MergedActivity {
  mostRecentActivity: ActivityExtended;
  relatedActivities: ActivityExtended[];
}

export interface ActivitySectionInterface {
  name: string;
  activities: ActivityExtended[];
}

interface ActivitySectionInterfaceWithDate extends ActivitySectionInterface {
  date: Date | null; // date used to bin the section, empty means "upcoming" section
}

interface ComponentState {
  activitiesLoading: boolean;
  activitiesLoadMore: boolean;
  activitiesChanged: boolean; // used to determine if the content has changed and the scroll position should be restored
  searchFilterInitialized: boolean;
  activitiesPagedResponseMetadata: PagedResponseMetadataInterface | null;
  activities: ActivityInterface[];
  mergedActivities: MergedActivity[];
  activitySections: ActivitySectionInterface[];

  lockedActivityEffectiveDate: Map<string, Date>;
  uniqueUserIds: string[];

  uniqueUsers: User[] | SimplifiedUser[];
  searchTerm: string;
  filters: GalaxyFilterInterface[];

  initialized: boolean;
}

const OpenOverdueTaskFilters: FilterGroupInterface = {
  operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
  filters: [
    {
      fieldId: StandardIds.ActivityTaskStatus,
      operator: FilterOperator.FILTER_OPERATOR_IS,
      values: [
        {
          string: ActivityTaskStatus.Open,
        },
      ],
    } as FilterInterface,
    {
      fieldId: StandardIds.ActivityTaskDueDate,
      operator: FilterOperator.FILTER_OPERATOR_IS_BEFORE,
      values: [
        {
          dateDefault: DateDefault.DATE_DEFAULT_TODAY,
        },
      ],
    } as FilterInterface,
  ],
};

@Injectable()
export class ActivitiesTimelineService {
  readonly upcomingSectionName = this.translateService.instant('CONTACT.ACTIVITIES_TIMELINE.SECTIONS.UPCOMING');
  readonly crmObjectId$$ = new BehaviorSubject<string | null>(null);
  readonly objectType$$ = new BehaviorSubject<ObjectType | ObjectAssociationType | null>(null);
  private destroyRef = inject(DestroyRef);
  private enableNewActivityFeed = toSignal(this.config?.enableNewActivityFeed$ ?? of(false));
  private activityCache: CacheService<ActivityService>;
  private contactCache: CacheService<ContactService>;
  private companyCache: CacheService<CompanyService>;

  constructor(
    public readonly state: RxState<ComponentState>,
    private readonly activityService: ActivityService,
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly iamService: IAMService,
    private readonly crmObjectService: CrmObjectService,
    private readonly contactService: ContactService,
    private readonly companyService: CompanyService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
  ) {
    this.state.set({
      activitiesLoading: false,
      activitiesLoadMore: false,
      activitiesChanged: false,
      activitiesPagedResponseMetadata: null,
      activities: [],
      activitySections: [],
      uniqueUserIds: [],
      searchTerm: '',
      filters: [],
      lockedActivityEffectiveDate: new Map<string, Date>(),
      initialized: false,
    });
    this.setupReloadActivities();
    this.setupLoadMore();
    this.setupUniqueUsers();

    this.activityCache = new CacheService<ActivityService>();
    this.activityCache.applyConfig(this.activityService, [
      {
        method: 'listActivities',
        keys: ['crmObjectId', 'crmObjectType', 'pagingOptions', 'search', 'filtersV2'],
        strategy: {
          maxAge: 5 * 60 * 1000,
          observable: { share: true },
        },
      },
    ]);

    this.contactCache = new CacheService<ContactService>();
    this.contactCache.applyConfig(this.contactService, [
      {
        method: 'getMultiObject',
        keys: ['crmObjectIds'],
        strategy: {
          maxAge: 5 * 60 * 1000,
          observable: { share: true },
        },
      },
    ]);

    this.companyCache = new CacheService<CompanyService>();
    this.companyCache.applyConfig(this.companyService, [
      {
        method: 'getMultiObject',
        keys: ['crmObjectIds'],
        strategy: {
          maxAge: 5 * 60 * 1000,
          observable: { share: true },
        },
      },
    ]);
  }

  getActivitySections(activities: ActivityExtended[], prioritizeOverdueTask: boolean): ActivitySectionInterface[] {
    const activitySections: ActivitySectionInterface[] = [];

    const activitiesWithEffectiveDate = activities.map((activity) => {
      const atv = this.toActivityWithEffectiveDate(activity);
      if (atv?.crmObjectId) {
        atv.effectiveDate = this.state.get('lockedActivityEffectiveDate').get(atv.crmObjectId) ?? atv.effectiveDate;
      }
      return atv;
    });

    let overdueTasks: ActivityExtendedWithEffectiveDate[] = [];
    if (prioritizeOverdueTask) {
      overdueTasks = activitiesWithEffectiveDate
        .filter((activity) => {
          return (
            this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityTaskStatus)?.stringValue ===
              ActivityTaskStatus.Open && activity.effectiveDate.getTime() < Date.now()
          );
        })
        .sort((a, b) => a.effectiveDate.getTime() - b.effectiveDate.getTime());
    }

    // split into upcoming and past activities
    const upcomingActivities = activitiesWithEffectiveDate.filter(
      (activity) => activity.effectiveDate.getTime() > Date.now(),
    );

    const overdueTasksAndUpcomingActivities = [...overdueTasks, ...upcomingActivities];

    const pastActivities = activitiesWithEffectiveDate.filter(
      (activity) => !overdueTasksAndUpcomingActivities?.find((item) => item.crmObjectId === activity.crmObjectId),
    );

    // add upcoming section if there are upcoming activities
    if (overdueTasksAndUpcomingActivities.length > 0) {
      activitySections.push({
        name: this.upcomingSectionName,
        activities: overdueTasksAndUpcomingActivities,
      });
    }

    // make a new section for each (month, year) pair
    const pastSectionsMap = new Map<number, ActivitySectionInterfaceWithDate>();
    for (const activity of pastActivities) {
      const startOfMonth = new Date(activity.effectiveDate.getFullYear(), activity.effectiveDate.getMonth());
      const startOfMonthTime = startOfMonth.getTime();
      if (!pastSectionsMap.has(startOfMonthTime)) {
        pastSectionsMap.set(startOfMonthTime, {
          name: `${startOfMonth.toLocaleString('default', { month: 'long' })} ${startOfMonth.getFullYear()}`,
          activities: [activity],
          date: startOfMonth,
        });
        continue;
      }
      pastSectionsMap.get(startOfMonthTime)?.activities.push(activity);
    }

    // map iteration undefined, so convert to descending array
    const pastSections = [...pastSectionsMap.values()].sort((a, b) => {
      if (a.date === null) {
        return -1;
      }
      if (b.date === null) {
        return 1;
      }
      if (a.date === b.date) {
        return 0;
      }
      return b.date.getTime() - a.date.getTime();
    });
    activitySections.push(...pastSections);

    return activitySections;
  }

  private toActivityWithEffectiveDate(crmActivity: ActivityInterface): ActivityExtendedWithEffectiveDate {
    const effectiveDate: Date | null =
      this.fieldService.getFieldValueFromCrmObject(crmActivity, SystemFieldIds.ActivityEffectiveDate)?.dateValue ??
      null;
    const isLoggedActivity =
      this.fieldService.getFieldValueFromCrmObject(crmActivity, StandardIds.ActivityManuallyLogged)?.booleanValue ??
      false;
    return {
      ...crmActivity,
      isLoggedActivity: isLoggedActivity,
      effectiveDate: effectiveDate ?? crmActivity.created ?? new Date(),
    };
  }

  initializeState(searchTerm: string, filters: GalaxyFilterInterface[]): void {
    this.state.set({
      searchTerm: searchTerm,
      filters: filters,
      initialized: true,
    });
  }

  setupLoadMore(): void {
    combineLatest([
      this.state.select('initialized'),
      this.state.select('activitiesLoadMore'),
      this.state.select('searchTerm'),
      this.state.select('filters'),
      this.crmObjectId$$.asObservable(),
      this.objectType$$.asObservable(),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(([initialized]) => !initialized),
        debounceTime(100),
        filter(([, activitiesLoadMore]) => activitiesLoadMore),
        // save the scroll position because Angular will reset it when the content changes
        tap(() => {
          this.state.set({ activitiesLoading: true });
        }),
        switchMap(([, , searchTerm, filters, crmObjectId, objectType]) => {
          const activitiesPagedResponseMetadata = this.state.get('activitiesPagedResponseMetadata');
          if (activitiesPagedResponseMetadata?.hasMore === false) {
            return of({});
          }
          const crmFilters = this.crmObjectService.convertFilters(filters);
          const request = {
            crmObjectId: crmObjectId,
            crmObjectType: objectType,
            pagingOptions: {
              pageSize: ACTIVITIES_TIMELINE_PAGE_SIZE,
              cursor: activitiesPagedResponseMetadata?.nextCursor ?? '',
            },
            search: {
              searchTerm: searchTerm,
            },
            filtersV2: this.addPredefinedFilters(crmFilters, !crmObjectId),
          } as ListActivitiesRequestInterface;

          const activities$ = this.getActivities(request);

          if (activitiesPagedResponseMetadata?.nextCursor || filters.length > 0 || searchTerm.length > 0) {
            return activities$;
          }

          let overdueTasks$: Observable<ListActivitiesResponseInterface> = of({ activities: [] });
          if (crmObjectId) {
            const overdueRequest = {
              crmObjectId: crmObjectId,
              crmObjectType: objectType,
              pagingOptions: {
                pageSize: 100,
                cursor: '',
              },
              search: {
                searchTerm: searchTerm,
              },
              filtersV2: OpenOverdueTaskFilters,
            } as ListActivitiesRequestInterface;
            overdueTasks$ = this.activityService.listActivities(overdueRequest);
          }

          return combineLatest([activities$, overdueTasks$]).pipe(
            map(([activities, overdueTasks]) => {
              const response = activities;
              const allActivities = [...(activities?.activities ?? []), ...(overdueTasks?.activities ?? [])];
              response.activities = allActivities;
              return response;
            }),
          );
        }),
        tap((response: ListActivitiesResponseInterface) => {
          if (!response) {
            return;
          }

          this.state.set({
            activitiesLoadMore: false,
            activitiesPagedResponseMetadata: {
              nextCursor: response?.pagingMetadata?.nextCursor ?? '',
              hasMore: response?.pagingMetadata?.hasMore ?? false,
            },
          });
        }),
        map((response) => response?.activities ?? []),
        tap((activities) => {
          const filteredActivities = this.filterActivities([...this.state.get('activities'), ...activities]);
          const mergedActivities = this.state.get('mergedActivities');
          const [updatedMergedActivities, updatedActivities] = this.mergeEmailActivities(
            filteredActivities,
            mergedActivities,
          );
          this.state.set({
            activities: updatedActivities,
            activitiesLoading: false,
            activitiesChanged: true,
            mergedActivities: updatedMergedActivities,
          });
        }),
      )
      .subscribe();
  }

  setupUniqueUsers(): void {
    this.state.connect(
      'uniqueUserIds',
      this.state.select('activities').pipe(
        map((activities) => {
          const uniqueUserIds = [...this.state.get('uniqueUserIds')];
          for (const activity of activities) {
            if (activity.ownerId && !uniqueUserIds.includes(activity.ownerId)) {
              uniqueUserIds.push(activity.ownerId);
            }
          }
          return uniqueUserIds;
        }),
      ),
    );

    this.state.connect(
      'uniqueUsers',
      this.state
        .select('uniqueUserIds')
        .pipe(
          startWith([] as string[]),
          pairwise(),
          map(([prev, curr]) => curr.filter((id) => !prev.includes(id))),
          filter((newIds) => newIds.length > 0),
          mergeMap((newIds) => getMultiUsers(newIds, this.config, this.iamService)),
          scan((acc, newUsers) => [...acc, ...newUsers] as User[] | SimplifiedUser[], [] as User[] | SimplifiedUser[]),
        ),
    );
  }

  setupReloadActivities(): void {
    combineLatest([this.crmObjectId$$.asObservable(), this.objectType$$.asObservable()])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
        tap(([crmObjectId, objectType]) => {
          if (crmObjectId && objectType) {
            // triggers initial load of activities
            this.reloadActivities();
          }
        }),
      )
      .subscribe();
  }

  loadMoreActivities(): void {
    const activitiesPagedResponseMetadata = this.state.get('activitiesPagedResponseMetadata');
    if (activitiesPagedResponseMetadata?.hasMore === false) {
      return;
    }
    this.state.set({
      activitiesLoadMore: true,
    });
  }

  reloadActivities(): void {
    this.state.set({
      activitiesLoading: false,
      activitiesLoadMore: true,
      activitiesChanged: false,
      activitiesPagedResponseMetadata: null,
      activities: [],
      activitySections: [],
      lockedActivityEffectiveDate: new Map<string, Date>(),
    });
  }

  filterActivities(activities: ActivityInterface[]): ActivityInterface[] {
    const activitiesWithEffectiveDateMap = new Map<string, ActivityExtendedWithEffectiveDate>();

    activities.forEach((activity) => {
      if (!activity.crmObjectId) {
        console.warn('Skipping CRM activity without crmObjectId', {
          externalId: activity.externalId,
          crmObjectSubtype: activity.crmObjectSubtype,
        });
        return;
      }
      activitiesWithEffectiveDateMap.set(activity.crmObjectId, this.toActivityWithEffectiveDate(activity));
    });

    return [...activitiesWithEffectiveDateMap.values()];
  }

  lockActivityEffectiveDate(activity: ActivityInterface): void {
    if (activity.crmObjectId) {
      const lockedActivityEffectiveDate = this.state.get('lockedActivityEffectiveDate');
      if (lockedActivityEffectiveDate.get(activity.crmObjectId)) {
        return; // already locked
      }
      let effectiveDate = activity.created ?? new Date();
      try {
        effectiveDate = this.toActivityWithEffectiveDate(activity).effectiveDate;
      } catch {
        // do nothing
      }

      lockedActivityEffectiveDate.set(activity.crmObjectId, effectiveDate);
      this.state.set({ lockedActivityEffectiveDate: lockedActivityEffectiveDate });
    }
  }

  addPredefinedFilters(
    filters: FilterGroupInterface | undefined,
    hideTaskAndSystemActivities: boolean,
  ): FilterGroupInterface | undefined {
    const predefFilter: FilterInterface[] = [
      {
        fieldId: 'FieldID-fb7ca0a7-e1a9-43a9-8ead-dd6ea20dda11',
        operator: 4,
        values: [{ string: 'UPDATED' }],
      },
    ];

    if (hideTaskAndSystemActivities) {
      if (!this.enableNewActivityFeed()) {
        predefFilter.push({
          fieldId: StandardIds.ActivityManuallyLogged,
          operator: 3,
          values: [{ boolean: true }],
        } as FilterInterface);
      }
      predefFilter.push({
        fieldId: SystemFieldIds.ActivityType,
        operator: 4,
        values: [{ string: 'Task' }],
      } as FilterInterface);
    }

    if (!filters) {
      filters = {
        operator: 1,
        filters: predefFilter,
      };
    } else {
      filters = {
        ...filters,
        filters: [...(filters.filters || []), ...predefFilter],
      };
    }

    return filters;
  }

  mergeEmailActivities(
    activities: ActivityExtended[],
    mergedActivities: MergedActivity[],
  ): [MergedActivity[], ActivityExtended[]] {
    const emailActivitiesMap = this.addEmailActivities(mergedActivities);

    activities.forEach((activity) => {
      const emailType = this.fieldService.getFieldValueFromCrmObject(
        activity,
        StandardIds.ActivityEmailType,
      )?.stringValue;
      const emailID = this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityEmailID)?.stringValue;

      if (
        emailID &&
        (emailType == 'marketing_campaign_email' ||
          emailType == 'yesware_campaign_email' ||
          emailType == 'yesware_tracked_email' ||
          emailType == 'user_provider_email')
      ) {
        const existingCampaignActivity = emailActivitiesMap.get(emailID) || {
          mostRecentActivity: activity,
          relatedActivities: [],
        };

        if (!emailActivitiesMap.has(emailID)) {
          existingCampaignActivity.relatedActivities.push(activity);
          emailActivitiesMap.set(emailID, existingCampaignActivity);
        } else {
          // Check if activity already exists in relatedActivities by crmObjectId
          const activityExists = existingCampaignActivity.relatedActivities.some(
            (relatedActivity) => relatedActivity.crmObjectId === activity.crmObjectId,
          );

          if (!activityExists) {
            existingCampaignActivity.relatedActivities.push(activity);
          }
        }
      }
    });

    const updatedMergedActivities = Array.from(emailActivitiesMap.values());
    return this.filterCampaignActivities(updatedMergedActivities, activities);
  }

  private addEmailActivities(mergedActivities: MergedActivity[]): Map<string, MergedActivity> {
    const existingCampaignActivities = new Map<string, MergedActivity>();
    mergedActivities?.forEach((activity) => {
      const emailID = this.fieldService.getFieldValueFromCrmObject(
        activity.mostRecentActivity,
        StandardIds.ActivityEmailID,
      )?.stringValue;
      if (emailID) {
        existingCampaignActivities.set(emailID, activity);
      }
    });
    return existingCampaignActivities;
  }

  private filterCampaignActivities(
    mergedActivities: MergedActivity[],
    activities: ActivityExtended[],
  ): [MergedActivity[], ActivityExtended[]] {
    const activitiesToRemove = new Set<string>();

    mergedActivities.forEach((activity) => {
      activity.relatedActivities.sort((a, b) => (b.updated?.getTime() ?? 0) - (a.updated?.getTime() ?? 0));

      if (activity.relatedActivities.length > 0) {
        // The most recent event is the headline; earlier events (sent, delivered, …)
        // stay in the related list.
        activity.mostRecentActivity = activity.relatedActivities[0];
        activity.relatedActivities = activity.relatedActivities.slice(1);

        // Check if the most recent activity has an empty subject or body
        let subject = this.fieldService.getFieldValueFromCrmObject(
          activity.mostRecentActivity,
          StandardIds.ActivityEmailSubject,
        )?.stringValue;

        let body = this.fieldService.getFieldValueFromCrmObject(
          activity.mostRecentActivity,
          StandardIds.ActivityEmailBody,
        )?.stringValue;

        let needSubject = !subject || subject.length === 0;
        let needBody = !body || body.length === 0;

        // If we need either subject or body, look through all related activities
        if (needSubject || needBody) {
          for (const relatedActivity of activity.relatedActivities) {
            // Skip if we already have both subject and body
            if (!needSubject && !needBody) break;

            // Check for subject if needed
            if (needSubject) {
              const relatedSubject = this.fieldService.getFieldValueFromCrmObject(
                relatedActivity,
                StandardIds.ActivityEmailSubject,
              )?.stringValue;

              if (relatedSubject && relatedSubject.length > 0) {
                activity.mostRecentActivity = this.fieldService.setFieldValueOnCrmObject(
                  activity.mostRecentActivity,
                  StandardIds.ActivityEmailSubject,
                  relatedSubject,
                );
                subject = relatedSubject;
                // No longer need subject
                needSubject = false;
              }
            }

            // Check for body if needed
            if (needBody) {
              const relatedBody = this.fieldService.getFieldValueFromCrmObject(
                relatedActivity,
                StandardIds.ActivityEmailBody,
              )?.stringValue;

              if (relatedBody && relatedBody.length > 0) {
                activity.mostRecentActivity = this.fieldService.setFieldValueOnCrmObject(
                  activity.mostRecentActivity,
                  StandardIds.ActivityEmailBody,
                  relatedBody,
                );
                body = relatedBody;
                // No longer need body
                needBody = false;
              }
            }
          }
        }
      }

      activity.relatedActivities.forEach((activity) => {
        if (!activity.crmObjectId) {
          throw new Error('activity has no crmObjectId');
        }
        activitiesToRemove.add(activity.crmObjectId);
      });
    });

    const filteredActivities = activities.filter(
      (activity) => activity.crmObjectId && !activitiesToRemove.has(activity.crmObjectId),
    );

    return [mergedActivities, filteredActivities];
  }

  getRelatedCampaignActivities(activityID: string | undefined): ActivityExtended[] {
    if (!activityID) {
      throw new Error('activity has no crmObjectId');
    }
    return (
      this.state
        .get('mergedActivities')
        ?.filter((campaignActivity) => campaignActivity.mostRecentActivity?.crmObjectId === activityID)
        .flatMap((campaignActivity) => campaignActivity.relatedActivities) ?? []
    );
  }

  updateActivity(activity: ActivityInterface): boolean {
    let updated = false;
    const activities = this.state.get('activities');
    const index = activities.findIndex((a) => a.crmObjectId === activity.crmObjectId);
    if (index >= 0) {
      this.lockActivityEffectiveDate(activities[index]);
      activities[index] = activity;
      this.state.set({ activities: [...activities] });
      updated = true;
    }
    return updated;
  }

  // Uses the activities service with cache already configured
  private getActivities(request: ListActivitiesRequestInterface): Observable<ListActivitiesResponseInterface> {
    return this.activityService.listActivities(request);
  }
}
